import React, { useEffect, Fragment } from 'react';
import { useQuery } from '@apollo/react-hooks';
import type { ApolloError } from 'apollo-client';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { markErrorAsHandled } from '@confluence/graphql';
import {
	ErrorDisplay,
	isUnauthorizedError,
	withErrorBoundary,
	Attribution,
} from '@confluence/error-boundary';
import { useSessionData } from '@confluence/session-data';
import { usePublicLinkPageDataProvider } from '@confluence/public-links';

import { analyticsEditPageScreenShown, analyticsViewPageScreenShown } from './analytics';
import { PageShownPublicLinksEventQuery } from './PageShownPublicLinksEvent.graphql';
import type {
	PageShownPublicLinksEventQuery as PageShownPublicLinksEventQueryType,
	PageShownPublicLinksEventQueryVariables,
} from './__types__/PageShownPublicLinksEventQuery';

export const PageShownPublicLinksEvent = withErrorBoundary({
	attribution: Attribution.EXTERNAL_COLLAB,
})((props: { contentId: string; isPage: boolean; isEditPage?: boolean }) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const sessionData = useSessionData();
	const publicLinkData = usePublicLinkPageDataProvider({
		pageId: props.contentId,
	});

	const {
		loading,
		data: queryData,
		error,
	} = useQuery<PageShownPublicLinksEventQueryType, PageShownPublicLinksEventQueryVariables>(
		PageShownPublicLinksEventQuery,
		{
			variables: { contentId: props.contentId },
			skip: !sessionData.isLoggedIn || !props.isPage,
		},
	);

	const space = queryData?.content?.nodes?.[0]?.space;
	const isSpaceOverview = space?.homepage?.id === props.contentId;

	useEffect(() => {
		if (!props.isPage || publicLinkData.isLoading || loading || !space || isSpaceOverview) return;
		const event = props.isEditPage ? analyticsEditPageScreenShown : analyticsViewPageScreenShown;
		event(createAnalyticsEvent, space.id, props.contentId, {
			isPublicLinkEnabled: !publicLinkData.isLoading && publicLinkData.isPublicLinkFullyEnabled,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [publicLinkData.isLoading, loading, props.contentId]);

	const errors = [error, ...publicLinkData.errors].filter(
		(err): err is ApolloError => err !== undefined,
	);

	useEffect(() => {
		for (const err of errors) {
			if (isUnauthorizedError(err)) {
				markErrorAsHandled(err);
			}
		}
	}, [errors]);

	if (errors.length > 0) {
		// No error UI is provided here, since these failures should be transparent to the user
		return (
			<Fragment>
				{errors.map((err, i) => (
					<ErrorDisplay key={i} error={err} />
				))}
			</Fragment>
		);
	}

	return null;
});
